import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiCalendar, FiClock, FiUsers, FiMapPin, FiEdit, FiTrash2, FiCheckCircle, FiAlertCircle, FiUserMinus, FiSave } = FiIcons;

const ExamDetailsModal = ({ isOpen, onClose, exam, onUpdate, onDelete, canManage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [absenteeCount, setAbsenteeCount] = useState(exam?.absentees || 0);
  const [showAbsenteeForm, setShowAbsenteeForm] = useState(false);

  if (!exam) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  const getEndTime = () => {
    const startTime = new Date(exam.date);
    const endTime = new Date(startTime.getTime() + exam.duration * 60000);
    return endTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleStatusChange = (newStatus) => {
    const updates = { status: newStatus };
    if (newStatus === 'completed') {
      updates.completedAt = new Date().toISOString();
      updates.actualTakers = exam.expectedTakers - (exam.absentees || 0);
    } else if (newStatus === 'in-progress') {
      updates.startedAt = new Date().toISOString();
    }
    onUpdate(exam.id, updates);
  };

  const handleAbsenteeSubmit = () => {
    onUpdate(exam.id, { 
      absentees: absenteeCount,
      actualTakers: exam.expectedTakers - absenteeCount
    });
    setShowAbsenteeForm(false);
  };

  const attendanceRate = exam.expectedTakers > 0 
    ? Math.round(((exam.expectedTakers - (exam.absentees || 0)) / exam.expectedTakers) * 100) 
    : 0;

  const { date, time } = formatDateTime(exam.date);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">{exam.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(exam.status)}`}>
                      {exam.status}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="mr-1" />
                      {exam.courseCode} - {exam.classCode}
                    </span>
                    <span className="flex items-center">
                      <SafeIcon icon={FiMapPin} className="mr-1" />
                      {exam.venue}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {canManage && (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit exam"
                      >
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button
                        onClick={() => onDelete(exam.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete exam"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Date and Time Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiCalendar} className="text-blue-600" />
                    <h4 className="font-medium text-gray-800">Date & Time</h4>
                  </div>
                  <p className="text-sm text-gray-600">{date}</p>
                  <p className="font-medium text-gray-900">{time} - {getEndTime()}</p>
                  <p className="text-xs text-gray-500 mt-1">{exam.duration} minutes</p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiUsers} className="text-green-600" />
                    <h4 className="font-medium text-gray-800">Attendance</h4>
                  </div>
                  <p className="text-sm text-gray-600">Expected: {exam.expectedTakers}</p>
                  <p className="font-medium text-gray-900">
                    Actual: {exam.actualTakers || exam.expectedTakers - (exam.absentees || 0)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {exam.absentees || 0} absentees • {attendanceRate}% attendance
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiMapPin} className="text-purple-600" />
                    <h4 className="font-medium text-gray-800">Venue</h4>
                  </div>
                  <p className="font-medium text-gray-900">{exam.venue}</p>
                  <p className="text-sm text-gray-600">{exam.examType}</p>
                </div>
              </div>

              {/* Quick Actions */}
              {canManage && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-3">Quick Actions</h4>
                  <div className="flex flex-wrap gap-2">
                    {exam.status === 'scheduled' && (
                      <>
                        <button
                          onClick={() => handleStatusChange('in-progress')}
                          className="px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center space-x-2"
                        >
                          <SafeIcon icon={FiClock} />
                          <span>Start Exam</span>
                        </button>
                        <button
                          onClick={() => handleStatusChange('completed')}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                        >
                          <SafeIcon icon={FiCheckCircle} />
                          <span>Mark Complete</span>
                        </button>
                      </>
                    )}
                    
                    {exam.status === 'in-progress' && (
                      <button
                        onClick={() => handleStatusChange('completed')}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiCheckCircle} />
                        <span>Mark Complete</span>
                      </button>
                    )}

                    <button
                      onClick={() => setShowAbsenteeForm(true)}
                      className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={FiUserMinus} />
                      <span>Update Absentees</span>
                    </button>

                    {exam.status !== 'cancelled' && (
                      <button
                        onClick={() => handleStatusChange('cancelled')}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiAlertCircle} />
                        <span>Cancel Exam</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Absentee Form */}
              {showAbsenteeForm && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-orange-50 border border-orange-200 p-4 rounded-lg"
                >
                  <h4 className="font-medium text-gray-800 mb-3">Update Absentee Count</h4>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Absentees
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={exam.expectedTakers}
                        value={absenteeCount}
                        onChange={(e) => setAbsenteeCount(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAbsenteeSubmit}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiSave} />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => setShowAbsenteeForm(false)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Invigilators */}
                {exam.invigilators && exam.invigilators.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Invigilators</h4>
                    <div className="space-y-1">
                      {exam.invigilators.map((invigilator, index) => (
                        <p key={index} className="text-sm text-gray-600">• {invigilator}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Materials */}
                {exam.materials && (
                  <div>
                    <h4 className="font-medium text-gray-800 mb-2">Required Materials</h4>
                    <p className="text-sm text-gray-600">{exam.materials}</p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              {exam.instructions && (
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Special Instructions</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{exam.instructions}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="font-medium text-gray-800 mb-2">Timeline</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Scheduled:</span>
                    <p>{new Date(exam.createdAt).toLocaleDateString()}</p>
                  </div>
                  {exam.startedAt && (
                    <div>
                      <span className="font-medium">Started:</span>
                      <p>{new Date(exam.startedAt).toLocaleString()}</p>
                    </div>
                  )}
                  {exam.completedAt && (
                    <div>
                      <span className="font-medium">Completed:</span>
                      <p>{new Date(exam.completedAt).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ExamDetailsModal;